import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { essentialsService, InboxItem } from '../../services/essentialsService';
import { ListPageHeader } from '../../components/crm/ListPageHeader';
import { CrmEmptyState } from '../../components/crm/EmptyState';
import ContactDetailPanel from '../../components/essentials/ContactDetailPanel';

function relTime(iso?: string | null): string {
  if (!iso) return '—';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 60) return `${Math.max(min, 0)}m ago`;
  if (min < 1440) return `${Math.round(min / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Essentials inbox: a filtered view over the two existing sources — Website Chat
 * (chatbot sessions) and Website Form (web_form contacts). Chat opens the
 * existing chat-inbox conversation; form opens the shared contact detail. No
 * second messaging surface is introduced.
 */
const EssentialsInbox: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openContactId, setOpenContactId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await essentialsService.getInbox();
      setItems(res.results);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = (item: InboxItem) => {
    if (item.kind === 'chat' && item.session_id) {
      navigate(`/chatbot/inbox/${item.session_id}`);
    } else if (item.contact_id) {
      setOpenContactId(item.contact_id);
    }
  };

  return (
    <div className="space-y-4">
      <ListPageHeader title="Inbox" subtitle="Website chats and form submissions in one place." />

      {loading ? (
        <div className="py-12 text-center text-fg-muted">Loading…</div>
      ) : items.length === 0 ? (
        <CrmEmptyState
          title="No messages yet"
          description="Website chats and form submissions will appear here — your form and chatbot are live and connected."
        />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-surface">
          {items.map((item, i) => (
            <li key={`${item.kind}-${item.session_id || item.contact_id || i}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${item.type === 'Website Chat' ? 'bg-primary/10 text-primary' : 'bg-warning-fg/10 text-warning-fg'}`}>
                    {item.type}
                  </span>
                  <span className="truncate font-medium text-fg">{item.name}</span>
                </div>
                <div className="text-xs text-fg-muted">
                  {relTime(item.timestamp)}
                  {item.assigned_user ? ` · ${item.assigned_user.email}` : ' · Unassigned'}
                </div>
              </div>
              <button type="button" onClick={() => open(item)} className="btn-secondary shrink-0 rounded-lg px-3 py-1.5 text-sm">
                {item.kind === 'chat' ? 'View conversation' : 'View'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {openContactId ? (
        <ContactDetailPanel contactId={openContactId} onClose={() => setOpenContactId(null)} onChanged={() => load()} />
      ) : null}
    </div>
  );
};

export default EssentialsInbox;
